import { cn } from '@/lib/utils';
import type { KeyboardEvent, ReactNode } from 'react';

type SettingsPageProps = {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
};

export function SettingsPage({ title, description, actions, children }: SettingsPageProps) {
  return (
    <div className="-mx-4 py-2">
      <div className="px-4 py-3 border-b bg-muted/20">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-light tracking-wide">{title}</h2>
            {actions ? <div className="-my-1 flex items-center gap-2 flex-wrap justify-end">{actions}</div> : null}
          </div>
          {description ? <p className="text-muted-foreground">{description}</p> : null}
        </div>
      </div>
      {children}
    </div>
  );
}

type SettingsSectionProps = {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
};

export function SettingsSection({ title, description, actions, children }: SettingsSectionProps) {
  return (
    <section className="border-b last:border-b-0">
      <div className="flex items-start justify-between gap-4 px-4 pt-5 pb-2">
        <div className="min-w-0">
          <h3 className="text-sm font-medium">{title}</h3>
          {description ? <p className="mt-0.5 text-xs text-muted-foreground">{description}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

export function SettingsRowList({ children }: { children: ReactNode }) {
  return <ul className="divide-y">{children}</ul>;
}

type SettingsRowProps = {
  as?: 'li' | 'div';
  leading?: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  detail?: ReactNode;
  trailing?: ReactNode;
  truncate?: boolean;
  onSelect?: () => void;
  selectLabel?: string;
};

export function SettingsRow({
  as: Element = 'li',
  leading,
  title,
  meta,
  detail,
  trailing,
  truncate = false,
  onSelect,
  selectLabel,
}: SettingsRowProps) {
  const selectable = onSelect
    ? {
        role: 'button',
        tabIndex: 0,
        onClick: onSelect,
        onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelect();
          }
        },
        'aria-label': selectLabel,
      }
    : {};

  return (
    <Element
      className={cn(
        'flex items-center gap-2 px-4 py-3',
        onSelect && 'hover:bg-muted/30 transition-colors cursor-pointer'
      )}
      {...selectable}
    >
      {leading}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className={cn('text-sm font-medium', truncate && 'truncate')}>{title}</div>
          {meta}
        </div>
        {detail ? <div className={cn('text-xs text-muted-foreground', truncate && 'truncate')}>{detail}</div> : null}
      </div>
      {trailing ? <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">{trailing}</div> : null}
    </Element>
  );
}

export function SettingsSectionBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('px-4 pb-4 pt-1 space-y-4', className)}>{children}</div>;
}

export function SettingsStateLine({ tone = 'muted', children }: { tone?: 'muted' | 'destructive'; children: ReactNode }) {
  return (
    <div className={cn('px-4 py-6 text-sm', tone === 'destructive' ? 'text-destructive' : 'text-muted-foreground')}>
      {children}
    </div>
  );
}
